import React, { useState } from 'react';
import { getPlayerData } from './services/api';
import { PET_EXPERIENCE_THRESHOLDS } from './data/petData';

function App() {
  const [username, setUsername] = useState('');
  const [playerData, setPlayerData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!username.trim()) return;

    setLoading(true);
    setError('');
    setPlayerData(null);

    try {
      const data = await getPlayerData(username.trim());
      setPlayerData(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Helper function to calculate pet level from experience
  const calculatePetLevel = (exp) => {
    if (!exp || exp < 0) return 1;
    const thresholds = PET_EXPERIENCE_THRESHOLDS;
    let level = 1;
    for (let i = 0; i < thresholds.length; i++) {
      if (exp >= thresholds[i]) {
        level = i + 1;
      } else {
        break;
      }
    }
    return Math.min(level, 100); // Cap at level 100
  };

  const renderPets = (pets) => {
    if (!Array.isArray(pets)) return <p>No pets data available</p>;
    
    return (
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: '15px' }}>
        {pets.map((pet, index) => {
          const calculatedLevel = calculatePetLevel(pet.exp || 0);
          return (
            <div key={index} style={{ border: '1px solid #ddd', padding: '15px', borderRadius: '8px', backgroundColor: pet.active ? '#e8f5e8' : '#fff' }}>
              <h4 style={{ margin: '0 0 10px 0', color: '#333' }}>{pet.type || 'Unknown Pet'}</h4>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                <span><strong>Tier:</strong></span>
                <span>{pet.tier || 'Unknown'}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                <span><strong>Level:</strong></span>
                <span>{calculatedLevel}</span>
              </div>
              {pet.exp && (
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                  <span><strong>Experience:</strong></span>
                  <span>{pet.exp.toLocaleString()}</span>
                </div>
              )}
              {pet.heldItem && (
                <div style={{ marginTop: '10px', padding: '8px', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
                  <strong>Held Item:</strong> {pet.heldItem}
                </div>
              )}
              {pet.active && <div style={{ textAlign: 'center', color: 'green', fontWeight: 'bold', marginTop: '10px' }}>Active Pet</div>}
            </div>
          );
        })}
      </div>
    );
  };

  const renderInventory = (inventoryData) => {
    if (!inventoryData || !inventoryData.data || !inventoryData.data.value || !inventoryData.data.value.i) {
      return <p>No inventory data available</p>;
    }
    
    const items = inventoryData.data.value.i.filter(item => item && item.value && Object.keys(item.value).length > 0);
    
    return (
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '10px' }}>
        {items.map((item, index) => {
          const itemData = item.value;
          const displayName = itemData.display && itemData.display.value && itemData.display.value.Name ? itemData.display.value.Name.value : (itemData.id ? itemData.id.value : 'Unknown Item');
          const count = itemData.Count ? itemData.Count.value : 1;
          
          return (
            <div key={index} style={{ border: '1px solid #ddd', padding: '10px', borderRadius: '5px', textAlign: 'center' }}>
              <div style={{ fontSize: '24px', marginBottom: '5px' }}>📦</div>
              <p style={{ fontSize: '12px', margin: '0' }}>{displayName}</p>
              {count > 1 && <p style={{ fontSize: '10px', color: '#666' }}>x{count}</p>}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div style={{ fontFamily: 'Arial, sans-serif', maxWidth: '1200px', margin: '0 auto', padding: '20px' }}>
      <header style={{ textAlign: 'center', marginBottom: '30px' }}>
        <h1 style={{ color: '#333' }}>SkyOptimizer</h1>
        <p>Enter a Minecraft username to view SkyBlock profile data</p>
      </header>

      <form onSubmit={handleSubmit} style={{ textAlign: 'center', marginBottom: '30px' }}>
        <input
          type="text"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          placeholder="Enter Minecraft username"
          style={{ padding: '10px', fontSize: '16px', width: '300px', marginRight: '10px', border: '1px solid #ccc', borderRadius: '4px' }}
        />
        <button
          type="submit"
          disabled={loading}
          style={{
            padding: '10px 20px',
            fontSize: '16px',
            backgroundColor: loading ? '#ccc' : '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: loading ? 'not-allowed' : 'pointer'
          }}
        >
          {loading ? 'Loading...' : 'Fetch Profile'}
        </button>
      </form>

      {error && (
        <div style={{ color: 'red', textAlign: 'center', marginBottom: '20px', padding: '10px', border: '1px solid red', borderRadius: '4px' }}>
          Error: {error}
        </div>
      )}

      {playerData && (
        <div>
          {playerData.members && Object.keys(playerData.members).length > 0 ? (
            Object.entries(playerData.members).map(([memberId, memberData]) => (
              <div key={memberId} style={{ marginBottom: '40px' }}>
                <h2 style={{ borderBottom: '2px solid #333', paddingBottom: '10px' }}>Profile Member: {memberId}</h2>
                
                {memberData.pets_data && (
                  <section style={{ marginBottom: '30px' }}>
                    <h3 style={{ color: '#007bff' }}>Pets</h3>
                    {renderPets(memberData.pets_data.pets)}
                  </section>
                )}
                
                {memberData.inventory && (
                  <div>
                    {memberData.inventory.inv_contents && (
                      <section style={{ marginBottom: '30px' }}>
                        <h3 style={{ color: '#007bff' }}>Inventory</h3>
                        {renderInventory(memberData.inventory.inv_contents)}
                      </section>
                    )}
                    
                    {memberData.inventory.ender_chest_contents && (
                      <section style={{ marginBottom: '30px' }}>
                        <h3 style={{ color: '#007bff' }}>Ender Chest</h3>
                        {renderInventory(memberData.inventory.ender_chest_contents)}
                      </section>
                    )}
                    
                    {memberData.inventory.inv_armor && (
                      <section style={{ marginBottom: '30px' }}>
                        <h3 style={{ color: '#007bff' }}>Armor</h3>
                        {renderInventory(memberData.inventory.inv_armor)}
                      </section>
                    )}
                    
                    {memberData.inventory.equipment_contents && (
                      <section style={{ marginBottom: '30px' }}>
                        <h3 style={{ color: '#007bff' }}>Equipment</h3>
                        {renderInventory(memberData.inventory.equipment_contents)}
                      </section>
                    )}
                    
                    {memberData.inventory.personal_vault_contents && (
                      <section style={{ marginBottom: '30px' }}>
                        <h3 style={{ color: '#007bff' }}>Personal Vault</h3>
                        {renderInventory(memberData.inventory.personal_vault_contents)}
                      </section>
                    )}
                    
                    {memberData.inventory.bag_contents && memberData.inventory.bag_contents.talisman_bag && (
                      <section style={{ marginBottom: '30px' }}>
                        <h3 style={{ color: '#007bff' }}>Talisman Bag</h3>
                        {renderInventory(memberData.inventory.bag_contents.talisman_bag)}
                      </section>
                    )}
                  </div>
                )}
                
                {!memberData.pets_data && !memberData.inventory && (
                  <p>No additional data available for this member</p>
                )}
              </div>
            ))
          ) : (
            <p style={{ textAlign: 'center' }}>No member data found in the profile</p>
          )}
        </div>
      )}
    </div>
  );
}

export default App;